import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  Fragment,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

import {
  Grid,
  Paper,
  TableContainer,
  TableHead,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Hidden,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import CreditCardIcon from "@material-ui/icons/CreditCard";

import Axios, { AxiosResponse } from "axios";

import { selectInformation, selectMoney } from "../Stores/informationSlice";
import AddCard from "../components/AddCard/AddCard";
import { IWorldPay } from "../Models/worldPay";
import visa from "../assets/img/visa.png";
import masterCard from "../assets/img/mastercard.png";
import maestro from "../assets/img/maestro.png";
import amex from "../assets/img/american-express.png";
import { selectDelivery } from "../Stores/deliverySlice";
import TimePicker from "../components/Common/TimePicker/TimePicker";
import CustomModal from "../components/Modal/CustomModal";
import PaymentSense from "../components/PaymentSense/PaymentSense";
import BackgroundImage from "../components/Common/BackgroundImage/BackgroundImage";
import { getLocalStorage, removeLocalStorage } from "../Services/common";
import { IOrderDisplay, ITableOrder } from "../Models/order";
import agent from "../Services/agent";
import { emptyCart } from "../Stores/redisCartSlice";
import { history } from "..";
import Loading from "../components/Loading/Loading";

import { PaymentStyles } from "./Styles/PaymentStyles";
import { GetStoreId } from "../Services/storeSession";

interface IProps {
  match: any;
}

const PaymentPage: React.FC<IProps> = ({ match }) => {
  const classes = PaymentStyles();
  const { t } = useTranslation();
  const orderId = match.params.orderId;
  const dispatch = useDispatch();
  const information = useSelector(selectInformation);
  const delivery = useSelector(selectDelivery);
  const money = useSelector(selectMoney);
  const [worldPayModal, setWorldPayModal] = useState(false);
  const [cartList, setCartList] = useState<IOrderDisplay>();
  const [cardType, setCardType] = useState("VISA");
  const [loading, setLoading] = useState<boolean>(true);
  const [dojoLoading, setDojoLoading] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");
  const [voucher, setVoucher] = useState<string>("");
  const [hasVoucher, setHasVoucher] = useState(false);
  const [step, setStep] = useState(15);
  const [startTime, setStartTime] = useState<Date>(
    new Date(
      new Date().getTime() +
        (delivery.delivery
          ? information.minimumDeliveryTime
          : information.minimumCollectionTime) *
          60000,
    ),
  );
  const [endTime, setEndTime] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const noteRef = useRef(note);
  const selectedTimeRef = useRef(selectedTime);
  const [disableTimePicker, setDisableTimePicker] = useState(
    information.preOrder && !information.openOrder
      ? false
      : information.openOrder
        ? true
        : false,
  );
  const [asap, setAsap] = useState(
    information.preOrder && !information.openOrder
      ? false
      : information.openOrder
        ? true
        : false,
  );
  const tableOrdering: ITableOrder = getLocalStorage("tableOrdering");

  const getHasVouchers = async () => {
    await agent.Voucher.hasActiveVoucher()
      .then((res) => {
        setHasVoucher(res);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const getTimes = async (pickupInStore: boolean) => {
    await agent.Setting.deliveryStartEndTime(pickupInStore)
      .then((res) => {
        if (!asap) {
          setSelectedTime(new Date(res.startTime));
        }
        setStartTime(new Date(res.startTime));
        setEndTime(new Date(res.endTime));
        setStep(res.preOrderTimeStep);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleShow = () => {
    setWorldPayModal(false);
  };

  const handleAddCard = async (form: IWorldPay) => {
    var axios = Axios.create();
    delete axios.defaults.headers.Authorization;
    axios({
      method: "post",
      url: "/tokens",
      baseURL: "https://api.worldpay.com/v1",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        Accept: "application/json",
      },
      data: {
        reusable: true,
        paymentMethod: {
          name: form.name,
          expiryMonth: form.date.substring(0, 2),
          expiryYear: form.date.substring(3, 7),
          issueNumber: 1,
          startMonth: 2,
          startYear: 2020,
          cardNumber: form.cardNumber,
          type: "Card",
          cvc: form.cvc,
        },
        clientKey: information.worldPayClientKey,
      },
    }).then((res: any) => {
      agent.Order.edit(
        {
          id: orderId,
          payType: "WorldPay",
          note: note,
          wPayToken: res.data.token,
          deliveryTime: selectedTime ? new Date(selectedTime) : null,
        },
        orderId,
      ).then((res) => {
        dispatch(emptyCart());
        history.push(`/ordering/order-status/${cartList?.id}/success`);
      });
    });
  };

  const getOrder = useCallback(async () => {
    await agent.Order.solo(orderId)
      .then((res) => {
        // Only orders still in "Register" can be paid here; anything else
        // (already paid, cancelled, completed, ...) goes back to the home page.
        if (res.orderStatusId !== "Register") {
          history.push("/");
          return;
        }
        setCartList(res);
        setLoading(false);
      })
      .catch((error) => {
        // Nothing on this page can render without the order, so never leave the spinner up:
        // send the customer home rather than letting a bad id hang the checkout.
        console.log(error);
        toast.error(t("somethingWentWrongTryAgain"));
        history.push("/");
      });
  }, [orderId, t]);

  // The customer can leave for Dojo's hosted checkout, pay, and still come back here with the
  // browser back button (error page, stuck loading, ...). The Dojo webhook can lag minutes behind
  // the actual capture, so before trusting any cash choice we ask the server to check the pending
  // Dojo intents live. "captured" means the money already left their card.
  const checkDojoPayment = useCallback(async (): Promise<
    "captured" | "processing" | "none"
  > => {
    if (information.dojo !== true) return "none";
    try {
      const res = await agent.Payments.checkDojoOrderPayment(orderId);
      return res.status;
    } catch {
      return "none";
    }
  }, [information.dojo, orderId]);

  // Same guard as Dojo above, but for PayPal: the customer can approve the popup and still end
  // up back here (closed tab, crash, network drop before the capture POST completes). "captured"
  // means PayPal already has the money even though our own confirm call never landed.
  const checkPayPalPayment = useCallback(async (): Promise<
    "captured" | "processing" | "none"
  > => {
    if (information.payPal !== true) return "none";
    try {
      const res = await agent.Payments.checkPayPalOrderPayment(orderId);
      return res.status;
    } catch {
      return "none";
    }
  }, [information.payPal, orderId]);

  const goToSuccess = useCallback(() => {
    dispatch(emptyCart());
    removeLocalStorage("tableOrdering");
    history.push(`/ordering/order-status/${orderId}/success`);
  }, [dispatch, orderId]);

  const handleCashPayment = async () => {
    setLoading(true);

    const dojoStatus = await checkDojoPayment();
    if (dojoStatus === "captured") {
      goToSuccess();
      return;
    }
    if (dojoStatus === "processing") {
      setLoading(false);
      toast.warn(
        "Your card payment is still being processed. Please wait a moment and try again.",
      );
      return;
    }

    agent.Order.edit(
      {
        id: orderId,
        payType: !tableOrdering
          ? delivery.delivery
            ? "CashOnDelivery"
            : "CashOnCollection"
          : "Cash",
        note: note,
        deliveryTime: selectedTime ? new Date(selectedTime) : null,
      },
      orderId,
    )
      .then(() => {
        goToSuccess();
      })
      .catch((e) => {
        toast.error(e);
        setLoading(false);
        history.push(`/`);
      });
  };

  const handleASAP = () => {
    if (asap) {
      setSelectedTime(startTime);
      getTimes(!delivery.delivery);
    } else setSelectedTime(null);
    setAsap(!asap);
    setDisableTimePicker(!disableTimePicker);
  };

  const applyVoucherDiscountHandler = () => {
    agent.Order.applyVoucherDiscount({
      orderId: orderId,
      voucherCode: voucher,
    })
      .then((res) => {
        if (res) {
          window.location.reload();
        }
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  useEffect(() => {
    noteRef.current = note;
  }, [note]);

  useEffect(() => {
    selectedTimeRef.current = selectedTime;
    agent.Order.updateDeliveryTime(
      {
        id: orderId,
        notes: noteRef.current,
        deliveryTime: selectedTime,
      },
      orderId,
    ).catch((e) => console.log(e));
  }, [selectedTime, orderId]);

  useEffect(() => {
    getTimes(!delivery.delivery);
    getOrder();
    getHasVouchers();
  }, [getOrder]);

  useEffect(() => {
    // Runs on mount and again on back/forward-cache restores (pageshow with persisted=true),
    // which skip a normal remount: if the customer comes back from Dojo after a successful
    // payment, take them straight to the success page instead of offering to pay again.
    const redirectIfCaptured = () => {
      Promise.all([checkDojoPayment(), checkPayPalPayment()]).then(
        ([dojoStatus, payPalStatus]) => {
          if (dojoStatus === "captured" || payPalStatus === "captured") {
            goToSuccess();
          }
        },
      );
    };

    redirectIfCaptured();

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) redirectIfCaptured();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [checkDojoPayment, checkPayPalPayment, goToSuccess]);

  if (loading || !cartList || dojoLoading) return <Loading />;
  return (
    <Fragment>
      <CustomModal
        title={t("worldPay")}
        body={<AddCard cardType={cardType} handleSubmit={handleAddCard} />}
        handleShow={handleShow}
        open={worldPayModal}
        action={null}
      />
      {information.themeDisplay !== "1" && <BackgroundImage />}
      <Grid
        container
        justifyContent="center"
        component={Paper}
        className={classes.Payment2}
      >
        <Grid item xs={12} sm={6} xl={6}>
          <h3>{t("confirmYourOrder")}</h3>
          <p>{t("checkoutYourOrder")}</p>
        </Grid>
        <Grid item xs={12} sm={6} xl={6} className="back">
          <Button
            onClick={() => history.goBack()}
            variant="contained"
            color="primary"
            size="large"
          >
            {t("back")}
          </Button>
        </Grid>

        <Grid item xs={12} xl={6}>
          <Hidden xsDown>
            <TableContainer className="productTable" component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("qty")}</TableCell>
                    <TableCell>{t("product")}</TableCell>
                    <TableCell>{t("price")}</TableCell>
                    <TableCell>{t("discount")}</TableCell>
                    <TableCell align="right">{t("total")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartList.orderItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>
                        {item.productName}
                        <ul>
                          {item.orderItemProductAttributeValueMappings
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map((att) => (
                              <li key={att.id}>
                                {att.productAttributeValue.name}
                              </li>
                            ))}
                        </ul>
                        {item.notes}
                      </TableCell>
                      <TableCell>
                        {money(item.price)}
                        <ul>
                          {item.orderItemProductAttributeValueMappings.map(
                            (att) => (
                              <li key={att.id}>
                                <span>
                                  {att.productAttributeValuePriceAdjustment ===
                                  0 ? (
                                    <span>.</span>
                                  ) : (
                                    money(
                                      att.productAttributeValuePriceAdjustment,
                                    )
                                  )}
                                </span>
                              </li>
                            ),
                          )}
                        </ul>
                      </TableCell>
                      <TableCell>
                        {item.discountTitle ? item.discountTitle : "-"}
                      </TableCell>
                      <TableCell align="right" className="totalBold">
                        {money(
                          (item.orderItemProductAttributeValueMappings
                            .map(
                              (attr) =>
                                attr.productAttributeValuePriceAdjustment,
                            )
                            .reduce((prev, curr) => prev + curr, 0) +
                            item.price) *
                            item.quantity,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className={classes.total}>
                    <TableCell
                      colSpan={3}
                      rowSpan={delivery.delivery ? 5 : 4}
                    />
                    <TableCell>{t("subTotal")}</TableCell>
                    <TableCell align="right">
                      {money(cartList.orderTotal)}
                    </TableCell>
                  </TableRow>
                  {delivery.delivery && (
                    <TableRow className={classes.total}>
                      <TableCell>{t("deliveryFee")}</TableCell>
                      <TableCell align="right">
                        {money(cartList.deliveryCharge)}
                      </TableCell>
                    </TableRow>
                  )}
                  {cartList.serviceCharge > 0 && (
                    <TableRow className={classes.total}>
                      <TableCell>{t("serviceCharge")}</TableCell>
                      <TableCell align="right">
                        {money(cartList.serviceCharge)}
                      </TableCell>
                    </TableRow>
                  )}
                  {cartList.packagingPrice > 0 && (
                    <TableRow className={classes.total}>
                      <TableCell>{t("packagingPrice")}</TableCell>
                      <TableCell align="right">
                        {money(cartList.packagingPrice)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow className="discount">
                    <TableCell>{t("discount")}</TableCell>
                    <TableCell align="right">
                      <span
                        style={{
                          float:
                            information.lang === "Persian" ? "left" : "none",
                        }}
                      >
                        {"-"}&nbsp;
                      </span>
                      {money(cartList.discount)}
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.total}>
                    <TableCell>{t("total")}</TableCell>
                    <TableCell align="right">
                      {money(
                        cartList.serviceCharge +
                          cartList.packagingPrice +
                          cartList.orderTotal -
                          cartList.discount +
                          (delivery.delivery ? cartList.deliveryCharge : 0),
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Hidden>
          <Hidden smUp>
            <TableContainer
              component={Paper}
              className={classes.productTableXS}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">{t("qty")}</TableCell>
                    <TableCell>{t("product")}</TableCell>
                    <TableCell>{t("price")}</TableCell>
                    <TableCell align="right">{t("total")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartList.orderItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell>
                        {item.productName}
                        <ul>
                          {item.orderItemProductAttributeValueMappings
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map((att) => (
                              <li key={att.id}>
                                {att.productAttributeValue.name}
                              </li>
                            ))}
                        </ul>
                        {item.notes}
                      </TableCell>
                      <TableCell>
                        {money(item.price)}
                        <ul>
                          {item.orderItemProductAttributeValueMappings.map(
                            (att) => (
                              <li key={att.id}>
                                <span>
                                  {att.productAttributeValuePriceAdjustment ===
                                  0 ? (
                                    <span>.</span>
                                  ) : (
                                    money(
                                      att.productAttributeValuePriceAdjustment,
                                    )
                                  )}
                                </span>
                              </li>
                            ),
                          )}
                        </ul>
                      </TableCell>
                      <TableCell align="right" className="totalBold">
                        {money(
                          (item.orderItemProductAttributeValueMappings
                            .map(
                              (attr) =>
                                attr.productAttributeValuePriceAdjustment,
                            )
                            .reduce((prev, curr) => prev + curr, 0) +
                            item.price) *
                            item.quantity,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} />
                  </TableRow>
                  <TableRow className={classes.total}>
                    <TableCell colSpan={2}>{t("subTotal")}</TableCell>
                    <TableCell colSpan={2} align="right">
                      {money(cartList.orderTotal)}
                    </TableCell>
                  </TableRow>
                  {delivery.delivery && (
                    <TableRow className={classes.total}>
                      <TableCell colSpan={2}>{t("deliveryFee")}</TableCell>
                      <TableCell colSpan={2} align="right">
                        {money(cartList.deliveryCharge)}
                      </TableCell>
                    </TableRow>
                  )}
                  {cartList.serviceCharge > 0 && (
                    <TableRow className={classes.total}>
                      <TableCell colSpan={2}>{t("serviceCharge")}</TableCell>
                      <TableCell colSpan={2} align="right">
                        {money(cartList.serviceCharge)}
                      </TableCell>
                    </TableRow>
                  )}
                  {cartList.packagingPrice > 0 && (
                    <TableRow className={classes.total}>
                      <TableCell colSpan={2}>{t("packagingPrice")}</TableCell>
                      <TableCell colSpan={2} align="right">
                        {money(cartList.packagingPrice)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow className="discount">
                    <TableCell colSpan={2}>{t("discount")}</TableCell>
                    <TableCell colSpan={2} align="right">
                      <span
                        style={{
                          float:
                            information.lang === "Persian" ? "left" : "none",
                        }}
                      >
                        {"-"}&nbsp;
                      </span>
                      {money(cartList.discount)}
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.total}>
                    <TableCell colSpan={2}>{t("total")}</TableCell>
                    <TableCell colSpan={2} align="right">
                      {money(
                        cartList.serviceCharge +
                          cartList.packagingPrice +
                          cartList.orderTotal -
                          cartList.discount +
                          cartList.deliveryCharge,
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Hidden>
        </Grid>
        <Grid item container xs={12} xl={6} justifyContent="center">
          <Grid item xs={12} sm={6} xl={10}>
            {!tableOrdering &&
              !(
                information.storeType === "Kiosk" ||
                information.storeType === "OriginKiosk"
              ) && (
                <div className={classes.timePicker}>
                  <TimePicker
                    label={
                      delivery.delivery
                        ? t("deliveryTime")
                        : t("collectionTime")
                    }
                    selectedTime={selectedTime ?? startTime}
                    setSelectedTime={setSelectedTime}
                    minTime={startTime}
                    maxTime={endTime}
                    isDisable={disableTimePicker}
                    step={step}
                  />

                  {(information.preOrder && !information.openOrder
                    ? false
                    : information.openOrder
                      ? true
                      : false) && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={asap}
                          onChange={handleASAP}
                          name="asap"
                          color="primary"
                        />
                      }
                      label={t("asap")}
                      className={classes.asap}
                    />
                  )}
                </div>
              )}
            {hasVoucher && (
              <Grid
                item
                xs={12}
                container
                alignItems="baseline"
                className={classes.voucherCodeGrid}
              >
                <h6>{t("discount")}:</h6>
                <TextField
                  onChange={(e) => {
                    setVoucher(e.target.value);
                  }}
                  value={voucher}
                  label={t("voucherCode")}
                  variant="outlined"
                  margin="normal"
                  className="voucherCodeTextField"
                />
                <Button
                  onClick={applyVoucherDiscountHandler}
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  {t("applyVoucherCode")}
                </Button>
              </Grid>
            )}

            {!(
              information.storeType === "Kiosk" ||
              information.storeType === "OriginKiosk"
            ) && (
              <div className={classes.deliverNote}>
                <TextField
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                  }}
                  label={t("note")}
                  multiline
                  rows={2}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              </div>
            )}
          </Grid>
          <Grid item xs={12} sm={6} xl={10} className="total">
            {!tableOrdering &&
            !(
              information.storeType === "Kiosk" ||
              information.storeType === "OriginKiosk"
            ) ? (
              delivery.delivery ? null : (
                <h4>{t("youCanJustCollectYourOrder")}.</h4>
              )
            ) : null}
            {information.payCash && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCashPayment}
                  className="button"
                >
                  {!tableOrdering
                    ? delivery.delivery
                      ? t("payOnDelivery")
                      : t("payOnCollection")
                    : t("payByCash")}
                </Button>
                {delivery.delivery &&
                  GetStoreId() === "08ca2fe0-9bd6-4511-8963-ebca07a7932f" && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCashPayment}
                      className="button"
                    >
                      {delivery.delivery
                        ? "Pay on delivery by card"
                        : t("payOnCollection")}
                    </Button>
                  )}
              </>
            )}
            {information.evoPayment && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  agent.Payments.RedirectEvoPayment(orderId).then((res) => {
                    dispatch(emptyCart());
                    removeLocalStorage("tableOrdering");
                    window.location.href = res;
                  });
                }}
                className="button"
              >
                Debit Or Credit Card Payment
              </Button>
            )}
            {information.noCheck && information.noCheckMerchantId && (
              <form method="POST" action="https://secure.nochex.com/">
                <input
                  type="hidden"
                  name="merchant_id"
                  value={information.noCheckMerchantId}
                />
                {/* 
                <input
                  type="hidden"
                  name="api_key"
                  value="nvk8c26b2c47536454baea7dc69dceb082c"
                /> */}

                {/* <input type="hidden" name="test_transaction" value="100" /> */}
                <input
                  type="hidden"
                  name="amount"
                  value={
                    cartList.serviceCharge +
                    cartList.packagingPrice +
                    cartList.orderTotal -
                    cartList.discount +
                    cartList.deliveryCharge
                  }
                />
                <input
                  type="hidden"
                  name="hide_billing_details "
                  value="true"
                />
                <input type="hidden" name="order_id" value={cartList.id} />
                <input
                  type="hidden"
                  name="success_url"
                  value={`${
                    window.location.origin
                  }/ordering/order-status/${orderId}/success/nochex/${
                    selectedTime ? selectedTime.getTime() : null
                  }/${note}`}
                />
                <input
                  type="hidden"
                  name="cancel_url"
                  value={`${
                    window.location.origin
                  }/ordering/order-status/${orderId}/cancel/nochex/${
                    selectedTime ? selectedTime.getTime() : null
                  }/${note}`}
                />
                <input
                  type="hidden"
                  name="callback_url"
                  value={`${
                    window.location.origin
                  }/ordering/order-status/${orderId}/success/nochex/${
                    selectedTime ? selectedTime.getTime() : null
                  }/${note}`}
                />

                {/* <input
                  type="hidden"
                  name="billing_fullname"
                  value="New Customer"
                />
                <input
                  type="hidden"
                  name="billing_address"
                  value="1 Leeds Street"
                />
                <input type="hidden" name="billing_address" value="Leeds" />
                <input type="hidden" name="billing_postcode" value="LS99 1NC" />
                <input
                  type="hidden"
                  name="customer_phone_number"
                  value="01134320987"
                /> */}
                <Button
                  variant="contained"
                  color="primary"
                  className="button"
                  type="submit"
                >
                  Pay on Credit or Debit Card
                </Button>
              </form>
            )}
            {information.dojo === true && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                className="button"
                onClick={async () => {
                  setDojoLoading(true);
                  try {
                    const url = await agent.Order.goToDojoPayment(
                      {
                        addressId: cartList.billingAddressId,
                        note: note,
                        deliveryTime: selectedTime,
                      },
                      orderId,
                    );
                    window.location.href = url;
                  } catch {
                    setDojoLoading(false);
                  }
                }}
              >
                <CreditCardIcon style={{ marginRight: 8 }} />
                Pay on Credit or Debit Card
              </Button>
            )}
            {information.paymentSense &&
              (information.dojo === true ? (
                <></>
              ) : (
                <PaymentSense
                  orderId={orderId}
                  value={
                    cartList.serviceCharge +
                    cartList.packagingPrice +
                    cartList.orderTotal -
                    cartList.discount +
                    cartList.deliveryCharge
                  }
                  selectedTime={selectedTime}
                  note={note}
                />
              ))}
            {information.worldPayClientKey !== "" && information.worldPay && (
              <Grid
                container
                justifyContent="space-around"
                className={classes.cards}
              >
                <Button
                  variant="contained"
                  color="default"
                  onClick={() => {
                    setWorldPayModal(true);
                    setCardType("VISA");
                  }}
                >
                  <img
                    width="100%"
                    style={{ cursor: "pointer" }}
                    src={visa}
                    alt="visa"
                  />
                </Button>
                <Button
                  variant="contained"
                  color="default"
                  onClick={() => {
                    setWorldPayModal(true);
                    setCardType("MSCD");
                  }}
                >
                  <img
                    width="100%"
                    style={{ cursor: "pointer" }}
                    src={masterCard}
                    alt="masterCard"
                  />
                </Button>
                <Button
                  variant="contained"
                  color="default"
                  onClick={() => {
                    setWorldPayModal(true);
                    setCardType("MAES");
                  }}
                >
                  <img
                    width="100%"
                    style={{ cursor: "pointer" }}
                    src={maestro}
                    alt="maestro"
                  />
                </Button>
                <Button
                  variant="contained"
                  color="default"
                  onClick={() => {
                    setWorldPayModal(true);
                    setCardType("AMEX");
                  }}
                >
                  <img
                    width="100%"
                    style={{ cursor: "pointer" }}
                    src={amex}
                    alt="amex"
                  />
                </Button>
              </Grid>
              // <button
              //   onClick={() => {
              //     setWorldPayModal(true);
              //   }}
              //   className="button"
              // >
              //   World Pay
              // </button>
            )}
            {information.payPal && information.payPalClientId !== "" && (
              <PayPalScriptProvider
                options={{
                  "client-id": information.payPalClientId,
                  currency: information.currencyDisplay,
                  components: "buttons,applepay",
                }}
              >
                <PayPalButtons
                  style={{ layout: "vertical", color: "blue" }}
                  onApprove={(_, actions: any) => {
                    // This function captures the funds from the transaction.
                    return actions?.order?.capture()?.then(function (
                      details: any,
                    ) {
                      // This function shows a transaction success message to your buyer.
                      // alert('Transaction completed by ' + details.payer.name.given_name);
                      setLoading(true);
                      agent.Order.edit(
                        {
                          id: orderId,
                          payType: "PayPal",
                          payPalOrderId: details.id,
                          note: noteRef.current,
                          deliveryTime: selectedTimeRef.current
                            ? new Date(selectedTimeRef.current)
                            : null,
                        },
                        orderId,
                      )
                        .then(() => {
                          dispatch(emptyCart());
                          setLoading(false);
                          history.push(
                            `/ordering/order-status/${cartList.id}/success`,
                          );
                        })
                        .catch((error: AxiosResponse) => {
                          agent.Order.edit(
                            {
                              id: orderId,
                              payType: "PayPal",
                              payPalOrderId: details.id,
                              note: noteRef.current,
                              deliveryTime: selectedTimeRef.current
                                ? new Date(selectedTimeRef.current)
                                : null,
                            },
                            orderId,
                          )
                            .then(() => {
                              dispatch(emptyCart());
                              setLoading(false);
                              history.push(
                                `/ordering/order-status/${cartList.id}/success`,
                              );
                            })
                            .catch((error: any) => {
                              setLoading(false);

                              const err = error.data.errors.json;
                              toast.error(
                                `name=${err.Name},message=${err.Message}`,
                              );
                              toast.error(
                                "if your payment compeleted, call to website manager",
                              );
                              console.log(error);

                              // if (error.status === 409)
                              //   toast.error(
                              //     t("yourPurchaseHadProblemCallToManager")
                              //   );
                            });
                        });
                    });
                  }}
                  createOrder={() => {
                    // Created server-side (mirrors the Dojo flow) so the backend has the PayPal
                    // order id from the moment it exists, not only after the customer approves —
                    // otherwise an abandoned checkout leaves no record a PayPal order was ever
                    // attempted, and a retry a few minutes later can't tell it already succeeded.
                    return agent.Order.goToPayPalPayment(
                      {
                        addressId: cartList.billingAddressId,
                        note: noteRef.current,
                        deliveryTime: selectedTimeRef.current
                          ? new Date(selectedTimeRef.current)
                          : null,
                      },
                      orderId,
                    );
                  }}
                />
              </PayPalScriptProvider>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default PaymentPage;
